import { AspectRatio, AspectRatioNumbers, AspectRatioCSSValues, AspectRatioDisplayNames } from "../types/aspect-ratios";

/**
 * Constant object for aspect ratio keys (for referencing)
 */
export const ASPECT_RATIOS = {
  SQUARE: "square",
  LANDSCAPE: "landscape",
  PORTRAIT: "portrait",
  PORTRAIT_2_3: "portrait_2_3",
  FEED: "feed"
} as const;

/**
 * Array of all aspect ratios in preferred order
 */
export const ASPECT_RATIO_ORDER: AspectRatio[] = ["square", "landscape", "feed", "portrait", "portrait_2_3"];

/**
 * Default aspect ratios for new ad asset groups
 */
export const DEFAULT_ASPECT_RATIOS: AspectRatio[] = ["square", "landscape", "feed", "portrait", "portrait_2_3"];

/**
 * Aspect ratio values in standard format (width:height)
 */
export const ASPECT_RATIO_NUMBERS: AspectRatioNumbers = {
  square: "1:1",
  landscape: "1.9:1",
  portrait: "9:16",
  portrait_2_3: "2:3",
  feed: "4:5"
};

/**
 * Aspect ratio values in CSS format (width/height)
 */
export const ASPECT_RATIO_CSS_VALUES: AspectRatioCSSValues = {
  square: "1 / 1",
  landscape: "1.9 / 1",
  portrait: "9 / 16",
  portrait_2_3: "2 / 3",
  feed: "4 / 5"
};

/**
 * Human-readable display names for aspect ratios
 */
export const ASPECT_RATIO_DISPLAY_NAMES: AspectRatioDisplayNames = {
  square: "Square (1:1)",
  landscape: "Landscape (1.9:1)",
  portrait: "Portrait (9:16)",
  portrait_2_3: "Portrait (2:3)",
  feed: "Feed (4:5)"
};

/**
 * Numeric ratio values for each aspect ratio, used for classifying dimensions.
 * Some ratio names have multiple entries (e.g. landscape has 16:9 and 300:157).
 * Mirrors SUPPORTED_ASPECT_RATIOS in the backend (delorean/src/utils/aspect_ratio.py).
 */
export const ASPECT_RATIO_NUMERIC_VALUES: ReadonlyArray<{ name: AspectRatio; value: number }> = [
  { name: "landscape", value: 16 / 9 },
  { name: "landscape", value: 300 / 157 },
  { name: "portrait", value: 9 / 16 },
  { name: "portrait", value: 157 / 300 },
  { name: "square", value: 1 },
  { name: "feed", value: 4 / 5 },
  { name: "portrait_2_3", value: 2 / 3 }
];

/**
 * Returns the closest named aspect ratio for a numeric width/height ratio.
 */
export function closestAspectRatio(ratio: number): AspectRatio {
  let best: AspectRatio = "square";
  let bestDiff = Infinity;
  for (const entry of ASPECT_RATIO_NUMERIC_VALUES) {
    const diff = Math.abs(entry.value - ratio);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = entry.name;
    }
  }
  return best;
}

/**
 * Returns the preferred aspect ratio from a list of available ratios.
 * Priority order: square > landscape > feed > portrait
 *
 * This function is order-agnostic - it will always return based on priority
 * regardless of the order of the input array.
 */
export function getPreferredAspectRatio(
  availableAspectRatios: AspectRatio[],
  fallback: AspectRatio = "square"
): AspectRatio {
  if (!availableAspectRatios || availableAspectRatios.length === 0) {
    return fallback;
  }

  return ASPECT_RATIO_ORDER.find((ar) => availableAspectRatios.includes(ar)) ?? availableAspectRatios[0] ?? fallback;
}
